import { useEffect, useMemo, useState } from "react";
import DashboardContainer from "../../components/dashboard-container";
// import { KamanExportComponent } from "@yoctotta/kaman";
// import "@yoctotta/kaman/dist/esm/lib.css";
import * as Chart from "chart.js";
import Chatbot from "@yoctotta/chatbot";
import "@yoctotta/chatbot/dist/chatbot.css";
import { Box, Typography, useTheme } from "@mui/material";
import { kamanService } from "../../utils/services/kamanService";
import config from "../../config";
import ForecastDetailsModal from "../../components/forecast-details-modal";
import { useDarkMode } from "../../components/context/darkModeContext";

/**
 * The forecast AI agent (access-gated Kaman chatbot + save-forecast modal),
 * with no page chrome of its own — so it can be dropped into the standalone
 * `/forecast` route (below) or wherever else this exact expert
 * (`kaman_expert_forecast`) is wanted. `chatHeight` lets the host size the
 * chat window to whatever space it actually has.
 *
 * NOT the agent used by Planning > Forecast's "Custom" drawer — that's a
 * separate expert/component, ForecastPlannerAgent, on purpose.
 */
export const ForecastAgent = ({ chatHeight = "calc(100vh - 164px)" }) => {
  const [canUpgrade, setCanUpgrade] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [kamanToken, setKamanToken] = useState("");
  const [forecastModalOpen, setForecastModalOpen] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const client_id = localStorage.getItem("client_id");
  const { colors } = useDarkMode();

  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme?.palette?.primary?.main || defaultColor;
  const secondary_color = theme?.palette?.secondary?.main || defaultColor;
  const forecastInputFields = useMemo(
    () => [
      {
        name: "input",
        type: "object",
        description: "input for the workflow",
        required: true,
        innerFields: [
          {
            name: "horizon",
            type: "number",
            description: "Horizon of the forecast",
            required: true,
          },
          {
            name: "sku",
            type: "string",
            description: "SKU for which the forecast is generated",
          },
          {
            name: "type",
            type: "string",
            description:
              "Which type of forecast generated. enum: Daily/Weekly/Monthly",
            required: true,
          },
        ],
      },
      {
        name: "metrics",
        type: "object",
        description: "Metrics generated after the execution of workflow",
        required: true,
        innerFields: [
          {
            name: "total_on_hand",
            type: "number",
            description: "Total quantity currently available on hand",
            required: true,
          },
          {
            name: "total_in_transit",
            type: "number",
            description: "Total quantity currently in transit",
            required: true,
          },
          {
            name: "total_quantity",
            type: "number",
            description: "Total quantity (on hand + in transit)",
            required: false,
          },
          {
            name: "avg_daily_demand",
            type: "number",
            description: "Average daily demand",
            required: true,
          },
          {
            name: "lead_time_days",
            type: "number",
            description: "Lead time in days",
            required: true,
          },
          {
            name: "demand_variability_over_lead_time",
            type: "number",
            description: "Demand variability over the lead time period",
            required: false,
          },
          {
            name: "saftety_stock_computed",
            type: "number",
            description: "System computed safety stock",
            required: false,
          },
          {
            name: "safety_stock_override",
            type: "number",
            description: "Manually overridden safety stock value",
            required: false,
          },
          {
            name: "safety_stock_used",
            type: "number",
            description: "Final safety stock used for calculation",
            required: false,
          },
          {
            name: "reorder_point",
            type: "number",
            description: "Inventory level at which reorder is triggered",
            required: false,
          },
          {
            name: "recommended_order_qty",
            type: "number",
            description: "Recommended quantity to reorder",
            required: false,
          },
          {
            name: "total_forecast_for_horizon",
            type: "number",
            description: "Total forecasted demand for the planning horizon",
            required: false,
          },
          {
            name: "confidence_summary",
            type: "number",
            description: "Forecast confidence score or summary metric",
            required: false,
          },
          {
            name: "reorder_by_date",
            type: "string",
            format: "date",
            description: "Recommended reorder-by date (YYYY-MM-DD)",
            required: false,
          },
        ],
      },
      {
        name: "forecast_adjusted",
        type: "array",
        description: "Adjusted demand forecast for upcoming months",
        required: true,
        innerFields: {
          type: "object",
          innerFields: [
            {
              name: "date",
              type: "string",
              description: "Forecast month (YYYY-MM-DD)",
              required: true,
            },
            {
              name: "predicted_qty",
              type: "number",
              description: "Predicted demand quantity for the month",
              required: true,
            },
          ],
        },
      },
      {
        name: "inventory_supplier_config_changes",
        type: "array",
        description: "List of inventory or supplier configuration changes",
        required: false,
        innerFields: {
          type: "object",
          description: "Configuration change entry",
          innerFields: [
            {
              name: "field",
              type: "string",
              description: "suggested field",
              required: true,
            },
            {
              name: "current_value",
              type: "string",
              description: "Current value of the suggested field",
              required: true,
            },
            {
              name: "suggested_value",
              type: "string",
              description: "New suggested value for the field",
              required: true,
            },
            {
              name: "reason",
              type: "string",
              description:
                "Reason for suggesting  the new value over the current value",
              required: true,
            },
          ],
        },
      },
    ],
    [],
  );

  const uiTools = useMemo(
    () => [
      {
        name: "saveForecast",
        description: "Save the generated forecast",
        inputFields: forecastInputFields,
        outputFields: [],
        func: async (args) => {
          console.log("function call -> uiTools", args);
          setForecastData(args);
          setForecastModalOpen(true);

          return {
            success: true,
            message:
              "Forecast details displayed in modal. Awaiting user action.",
          };
        },
      },
    ],
    [forecastInputFields],
  );

  useEffect(() => {
    let roles = localStorage.getItem("roles");
    try {
      roles = JSON.parse(roles);
    } catch (error) {
      console.error("Error parsing roles from localStorage:", error);
    }
    if (roles && roles.find((role) => role.name === "Admin")) {
      setCanAccess(true);
    } else if (
      roles &&
      roles.find((role) => ["Client - Standard"].includes(role.name))
    ) {
      setCanAccess(false);
      setCanUpgrade(true);
    } else {
      setCanUpgrade(false);
      setCanAccess(false);
    }
  }, []);

  useEffect(() => {
    const fetchKamanToken = async () => {
      try {
        const response = await kamanService.generateToken();
        if (response && response.token) {
          setKamanToken(response.token);
          setCanAccess(true);
        }
      } catch (error) {
        console.error("Error fetching Kaman token:", error);
      }
    };

    fetchKamanToken();
  }, []);

  const handleDiscardForecast = () => {
    console.log("Forecast discarded");
    setForecastModalOpen(false);
    setForecastData(null);
  };

  return (
    <>
      {/* Overlay container with relative positioning */}
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        {/* The actual content behind the overlay */}
        {kamanToken && (
          <Box sx={{ filter: canAccess ? "none" : "blur(2px)" }}>
            <Chatbot
              baseUrl={config.kaman_baseUrl}
              token={kamanToken}
              expertId={config.kaman_expert_forecast.id}
              expertName={config.kaman_expert_forecast.name}
              uiTools={uiTools}
              theme={{
                primaryColor: primary_color,
                secondaryColor: secondary_color,
                textColor: "var(--text-primary)",
                backgroundColor: "var(--bg-primary)",
                borderColor: "var(--border-color)",
                borderRadius: "8px",
                fontFamily: "Poppins",
                // fontSize: "12px",
              }}
              showAppsSelection={false}
              style={{
                width: "100%",
                height: chatHeight,
                textAlign: "left",
              }}
            />
          </Box>
        )}

        {!canAccess ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "var(--bg-primary)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              opacity: 0.7,
            }}
          >
            <Typography
              variant="h2"
              component="div"
              sx={{
                color: "var(--text-primary)",
                fontWeight: "bold",
                textShadow: `1px 1px 3px var(--text-secondary)`,
                letterSpacing: "2px",
                mb: 2,
              }}
            >
              Setting Up AI-Powered Support
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: "var(--text-tertiary)",
                opacity: 0.8,
                textAlign: "center",
                maxWidth: "600px",
                px: 2,
              }}
            >
              You can now interact with our AI-powered support system. Ask any
              questions or get assistance with your logistics needs. The AI is
              here to help you streamline your operations and provide quick
              responses to your queries.
            </Typography>
          </Box>
        ) : !canAccess && canUpgrade ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              opacity: 0.7,
            }}
          >
            <Typography
              variant="h2"
              component="div"
              sx={{
                color: "var(--text-primary)",
                fontWeight: "bold",
                textShadow: "1px 1px 3px var(--text-secondary)",
                letterSpacing: "2px",
                mb: 2,
              }}
            >
              UPGRADE REQUIRED
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: "var(--text-tertiary)",
                opacity: 0.8,
                textAlign: "center",
                maxWidth: "600px",
                px: 2,
              }}
            >
              Upgrade your current subscription to use the AI-powered
              functionalities.
            </Typography>
          </Box>
        ) : !canAccess && !canUpgrade ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              opacity: 0.7,
            }}
          >
            <Typography
              variant="h2"
              component="div"
              sx={{
                color: "var(--text-primary)",
                fontWeight: "bold",
                textShadow: "1px 1px 3px var(--text-secondary)",
                letterSpacing: "2px",
                mb: 2,
              }}
            >
              ACCESS DENIED
            </Typography>
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: "var(--text-tertiary)",
                opacity: 0.8,
                textAlign: "center",
                maxWidth: "600px",
                px: 2,
              }}
            >
              You do not have permission to access this AI-powered feature.
              Please contact your administrator.
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>

      {/* Forecast Details Modal */}
      <ForecastDetailsModal
        open={forecastModalOpen}
        onClose={() => setForecastModalOpen(false)}
        forecastData={forecastData}
        onDiscard={handleDiscardForecast}
        client_id={client_id}
      />
    </>
  );
};

// Standalone page — same agent, wrapped in the normal dashboard chrome.
const Forecast = () => (
  <DashboardContainer
    filterButton={false}
    hideUploadButton
    hideAddButton
    hideFilterClient={true}
    hideAllButton
    hideGridList
  >
    <ForecastAgent />
  </DashboardContainer>
);

export default Forecast;
